import {
  experienceFeedbackDismissAtom,
  experienceFeedbackEligibleAtom,
  experienceFeedbackMarkSubmittedAtom,
  experienceFeedbackShowAtom,
  experienceFeedbackSnoozeAtom,
  experienceFeedbackSnoozeCountAtom,
  experienceFeedbackTriggeredThisSessionAtom,
  experienceFeedbackVisibleAtom,
  TRIGGER_QUERY_COUNT,
} from '@/atoms/experienceFeedbackAtoms';
import { snoozeEvidenceFeedback } from '@/components/chat/api/evidenceFeedbackApi';
import { IS_EVIDENCE_EXPERIENCE_FEEDBACK_ENABLED } from '@/components/constants/flags';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type Dispatch } from '@/store';
import { isEvidenceApiAvailable } from '@/utils/backendSurfaces';
import { logger } from '@/utils/logger/logger';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';

interface UseExperienceFeedbackTriggerOptions {
  /**
   * Number of completed AI responses in the current chat.
   * A completed response is one that has finished streaming.
   */
  queryCount: number;

  /**
   * Whether the AI is currently streaming a response.
   * The nudge should not appear while streaming.
   */
  isStreaming: boolean;

  /**
   * Whether the user is authenticated.
   * The nudge should only appear for logged-in users.
   */
  isAuthenticated: boolean;

  /**
   * The current chat ID.
   * The nudge should only appear when viewing an existing chat (not on new chat pages).
   */
  chatId?: string | null;
}

interface UseExperienceFeedbackTriggerResult {
  /**
   * Whether the nudge should be visible right now.
   */
  shouldShowNudge: boolean;

  /**
   * Manually show the nudge (called when trigger conditions are met).
   */
  showNudge: () => void;

  /**
   * Hide the nudge without affecting persistence state.
   */
  hideNudge: () => void;

  /**
   * Snooze the nudge (increment snooze count, hide for this session).
   * Also syncs to backend for persistence across devices.
   */
  snooze: () => Promise<void>;

  /**
   * Mark feedback as submitted (hide forever).
   */
  markSubmitted: () => void;

  /**
   * Current snooze count (for analytics).
   */
  snoozeCount: number;
}

/**
 * Hook to manage the trigger logic for the Experience Feedback nudge.
 *
 * The nudge appears when:
 * 0. Feature flag is enabled (release-evidence-experience-feedback)
 * 1. User has completed at least 2 queries (TRIGGER_QUERY_COUNT)
 * 2. AI is not currently streaming
 * 3. User is authenticated
 * 4. User has not submitted feedback before
 * 5. User has not snoozed 3+ times
 * 6. User is on a non-public chat page with an ID (e.g. /scribe/chat/{id}, not /chat/{id})
 * 7. Nudge hasn't been triggered this session already
 *
 * @see docs/design-docs/experience-feedback/README.md
 */
export const useExperienceFeedbackTrigger = ({
  queryCount,
  isStreaming,
  isAuthenticated,
  chatId,
}: UseExperienceFeedbackTriggerOptions): UseExperienceFeedbackTriggerResult => {
  const dispatch = useDispatch<Dispatch>();
  const router = useRouter();
  const { baseProps } = useEvidenceAnalytics();

  // Feature flag check — AND-ed with the surface: both the submit and the
  // snooze post to `/evidence/feedback`, part of ml-scribe's cloud-only evidence
  // aggregate, so on an on-prem build the nudge has nowhere to send an answer
  // and is never offered, however the flag resolves.
  const isFeatureEnabled =
    isEvidenceApiAvailable && IS_EVIDENCE_EXPERIENCE_FEEDBACK_ENABLED();

  // Atom values
  const isVisible = useAtomValue(experienceFeedbackVisibleAtom);
  const isEligible = useAtomValue(experienceFeedbackEligibleAtom);
  const snoozeCount = useAtomValue(experienceFeedbackSnoozeCountAtom);
  const triggeredThisSession = useAtomValue(experienceFeedbackTriggeredThisSessionAtom);

  // Atom setters
  const showNudgeAction = useSetAtom(experienceFeedbackShowAtom);
  const hideNudgeAction = useSetAtom(experienceFeedbackDismissAtom);
  const snoozeAction = useSetAtom(experienceFeedbackSnoozeAtom);
  const markSubmittedAction = useSetAtom(experienceFeedbackMarkSubmittedAtom);

  // Track whether we've shown the nudge for analytics
  const hasTrackedView = useRef(false);

  // Only show the nudge on non-public chat pages with a chat ID (e.g. /scribe/chat/{id})
  // The nudge should NOT appear on public /chat routes
  const pathname = router.pathname;
  const isNonPublicChatWithId =
    pathname.includes('/scribe/chat') &&
    Boolean(chatId);

  // Determine if trigger conditions are met
  const shouldTrigger =
    isFeatureEnabled &&
    queryCount >= TRIGGER_QUERY_COUNT &&
    !isStreaming &&
    isAuthenticated &&
    isNonPublicChatWithId &&
    isEligible &&
    !triggeredThisSession;

  // Auto-show when trigger conditions are first met
  useEffect(() => {
    if (shouldTrigger && !isVisible && !hasTrackedView.current) {
      showNudgeAction();
      hasTrackedView.current = true;

      // Track that the nudge was viewed
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceExperienceFeedbackViewed,
        props: {
          ...baseProps,
          query_count: queryCount,
          snooze_count: snoozeCount,
        },
      });
    }
  }, [
    shouldTrigger,
    isVisible,
    showNudgeAction,
    dispatch,
    baseProps,
    queryCount,
    snoozeCount,
  ]);

  // Wrapper for snooze that also tracks analytics and calls backend
  const snooze = useCallback(async () => {
    // Update local state immediately for responsive UI
    snoozeAction();

    // Track to PostHog client-side
    track({
      event_name: TrackingEventName.EvidenceExperienceFeedbackSnoozed,
      props: {
        ...baseProps,
        snooze_count: snoozeCount + 1, // After this snooze
        query_count: queryCount,
      },
    });

    // Also persist to backend (fire-and-forget, don't block UI)
    try {
      await snoozeEvidenceFeedback();
    } catch (error) {
      // Log but don't fail - localStorage is the primary source of truth
      logger.error('Failed to sync snooze to backend:', error);
    }
  }, [snoozeAction, dispatch, baseProps, snoozeCount, queryCount]);

  // Wrapper for dismiss that also tracks analytics
  const hideNudge = useCallback(() => {
    hideNudgeAction();

    track({ options: { amplitude: false },
      event_name: TrackingEventName.EvidenceExperienceFeedbackDismissed,
      props: {
        ...baseProps,
        query_count: queryCount,
      },
    });
  }, [hideNudgeAction, dispatch, baseProps, queryCount]);

  return {
    shouldShowNudge: isVisible,
    showNudge: showNudgeAction,
    hideNudge,
    snooze,
    markSubmitted: markSubmittedAction,
    snoozeCount,
  };
};
